import {Component, Input} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NutraPushBottomSheetComponent } from '../nutra-push-bottom-sheet/nutra-push-bottom-sheet.component';
import {EventLoggerService} from '@services/event-logger-service';
import {AppConfig} from '../../../../../app.config';
import {CsIconModule} from '@components/icon';
import {CAudioModule} from '@shared/c-audio/c-audio.module';
import {ConnectionService} from '@services/connection-service';
import {UserAllocatedDoctorService} from '@services/user-allocated-doctor-service/user-allocated-doctor.service';
import {ApiClientConstant} from '@cureskin/api-client';

export interface NutraPushBannerData {
  objectId: string;
  heading?: string;
  title: string;
  sub_title: string;
  bannerImage: string;
  coverImage: string;
  coverImageV2: string;
}

@Component({
  selector: 'nutra-push-banner',
  standalone: true,
  imports: [CommonModule, CsIconModule, CAudioModule],
  templateUrl: './nutra-push-banner.html',
})
export class NutraPushBannerComponent {
  @Input('nutraPushBannerData') nutraPushBannerData: NutraPushBannerData;
  @Input('nutraPushExperimentVariantData') nutraPushExperimentVariantData: any;
  @Input('nutraPushBottomSheetData') nutraPushBottomSheetData: any;
  @Input('cartItems') cartItems: any;
  @Input('regimensClass') regimensClass: string[] = [];
  drAudioNoteVariant: boolean = false;
  user: Table.User;
  userLanguage: string;
  drFaceAudioNote: string;
  drHairAudioNote: string;
  drInfo: {
    name: string;
    imageUrl: string;
  };
  showNewUI: boolean = false;

  constructor(
    private readonly bottomSheet: MatBottomSheet,
    public readonly appConfig: AppConfig,
    public readonly eventLogger: EventLoggerService,
    public readonly conn: ConnectionService,
    private readonly doctorService: UserAllocatedDoctorService,
  ){}

  async ngOnInit(): Promise<void> {
    await this.getUserInfo();
    await this.getDrInfo();
    this.nutraPushExperimentVariantData && this.getExperimentData();
    this.calculateShowNewUI();
  }

  openNutraBottomSheet(): void {
    this.bottomSheet.open(NutraPushBottomSheetComponent, {
      data: {bottomSheetData: this.nutraPushBottomSheetData, bannerData: this.nutraPushBannerData, cartItems: this.cartItems},
      panelClass: 'nutra-push-bottomsheet-panel',
    });
    const eventData = { type: this.nutraPushBottomSheetData.objectId, name: 'nutra-push-banner' }
    this.eventLogger.cleverTapEvent('click', JSON.stringify(eventData));
  }

  getExperimentData(): void {
    this.drAudioNoteVariant = this.nutraPushExperimentVariantData?.drAudioNoteVariant;
    const margId = this.nutraPushBottomSheetData?.margId;
    const language = this.userLanguage || ApiClientConstant.LanguageString.EN;

    if (margId && this.nutraPushExperimentVariantData?.nutraProductsData[margId]?.drAudioNote) {
      const audioData = this.nutraPushExperimentVariantData.nutraProductsData[margId].drAudioNote;

      // Get face audio with English fallback
      let faceAudioNote = audioData?.face?.[language];
      if (!faceAudioNote || !faceAudioNote.trim()) {
        faceAudioNote = audioData?.face?.[ApiClientConstant.LanguageString.EN];
      }
      this.drFaceAudioNote = faceAudioNote && faceAudioNote.trim() ? faceAudioNote : null;

      // Get hair audio with English fallback
      let hairAudioNote = audioData?.hair?.[language];
      if (!hairAudioNote || !hairAudioNote.trim()) {
        hairAudioNote = audioData?.hair?.[ApiClientConstant.LanguageString.EN];
      }
      this.drHairAudioNote = hairAudioNote && hairAudioNote.trim() ? hairAudioNote : null;
    } else {
      this.drFaceAudioNote = null;
      this.drHairAudioNote = null;
    }
  }

  calculateShowNewUI(): void {
    // First check if drAudioNoteVariant is true
    if (!this.drAudioNoteVariant) {
      this.showNewUI = false;
      return;
    }

    // Check if regimensClass exists and has values
    if (!this.regimensClass || this.regimensClass.length === 0) {
      this.showNewUI = false;
      return;
    }

    const hasFace = this.regimensClass.includes('FACE');
    const hasHair = this.regimensClass.includes('HAIR');

    // Case 1: Only FACE regimen or FACE+HAIR regimen
    if (hasFace) {
      this.showNewUI = !!(this.drFaceAudioNote);
      return;
    }

    // Case 2: Only HAIR regimen
    if (!hasFace && hasHair) {
      this.showNewUI = !!(this.drHairAudioNote);
      return;
    }

    // Default case: no FACE regimen or other cases
    this.showNewUI = false;
  }

  async getUserInfo(): Promise<void> {
    this.user = this.conn.getActingUser();
    this.userLanguage = this.user?.get('languagePreference');
  }

  async getDrInfo(): Promise<void> {
    const doctor = await this.doctorService.getAllocatedDoctor(true);
    this.drInfo = { name: doctor.DoctorDisplayName, imageUrl: doctor.doctorDisplayImage };
  }
}
